import { useEffect, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { money, fmtDateTime } from "../lib/format";
import { ClosedNoticeDialog } from "../components/ClosedNoticeDialog";
import { BarChart3, Banknote, Landmark, Users, Palmtree, ChevronDown, ChevronRight, FileDown, ShieldCheck, Mail } from "lucide-react";
import CompanyBreadcrumb from "../components/CompanyBreadcrumb";
import ReportingWorkspace from "../components/ReportingWorkspace";
import { useClientPerms } from "../lib/useClientPerms";
import { useSort, SortBtn } from "../lib/sortable";

const EMP_A = {
  name: (e) => e.employee_name || "", slips: (e) => e.slips || 0, gross: (e) => e.gross || 0,
  income_tax: (e) => e.income_tax || 0, cpp: (e) => (e.cpp || 0) + (e.cpp2 || 0), ei: (e) => e.ei || 0, net: (e) => e.net_pay || 0,
};
const VAC_A = {
  name: (e) => e.employee_name || "", rate: (e) => e.vacation_rate || 0, accrued: (e) => e.total_accrued || 0,
  paid: (e) => e.total_paid || 0, direct: (e) => e.total_paid_direct || 0, balance: (e) => e.current_balance || 0,
};
const WSIB_A = {
  name: (e) => e.employee_name || "", code: (e) => e.code || "", rate: (e) => Number(e.rate) || 0,
  q1: (e) => e.assessable?.[0] || 0, q2: (e) => e.assessable?.[1] || 0, q3: (e) => e.assessable?.[2] || 0, q4: (e) => e.assessable?.[3] || 0,
  assessable: (e) => e.total_assessable || 0, premium: (e) => e.total_premium || 0,
};
const STATUS_CLS = (status) =>
  status === "Filed" ? "border-emerald-500 bg-emerald-50 text-emerald-700"
    : status === "Overdue" ? "border-red-500 bg-red-50 text-red-700"
    : status === "Upcoming" ? "border-amber-500 bg-amber-50 text-amber-700"
    : "border-neutral-300 bg-neutral-50 text-muted-foreground";

export default function Reports() {
  const { user } = useAuth();
  const isMasterUser = user?.role === "super_admin" && (user?.staff_role || "master") === "master";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const reportsEdit = useClientPerms().canEdit("reports");
  const [year, setYear] = useState(new Date().getFullYear());
  const [scope, setScope] = useState("year"); // "year" | "run" | "range"
  const [range, setRange] = useState({ start: `${new Date().getFullYear()}-01-01`, end: new Date().toISOString().slice(0, 10) });
  const [runs, setRuns] = useState([]);
  const [runId, setRunId] = useState("");
  const [data, setData] = useState(null);
  const [vac, setVac] = useState(null);
  const [wsib, setWsib] = useState(null);
  const [wsibDlg, setWsibDlg] = useState(null);   // {companyId, year, period, label}
  const [openEmp, setOpenEmp] = useState(null);
  const [tab, setTab] = useState("reports");
  const empS = useSort(data?.employees || [], EMP_A, "name", "asc");
  const vacS = useSort(vac?.employees || [], VAC_A, "name", "asc");
  const wsibS = useSort(wsib?.employees || [], WSIB_A, "name", "asc");

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search).get("company");
    api.get("/companies").then((r) => { setCompanies(r.data); if (qs && r.data.some((c) => c.company_id === qs)) setCompanyId(qs); else if (r.data[0]) setCompanyId(r.data[0].company_id); });
  }, []);

  useEffect(() => {
    if (!companyId) { setRuns([]); return; }
    api.get(`/payroll/runs?company_id=${companyId}`).then((r) => {
      const list = (r.data || []).slice().sort((a, b) => (b.pay_date || "").localeCompare(a.pay_date || ""));
      setRuns(list);
      if (!list.length) setScope("year");
      setRunId((prev) => (list.some((x) => x.run_id === prev) ? prev : (list[0]?.run_id || "")));
    }).catch(() => setRuns([]));
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    api.get(`/reports?company_id=${companyId}&year=${year}`).then((r) => setData(r.data)).catch(() => setData(null));
    api.get(`/reports/vacation?company_id=${companyId}&year=${year}`).then((r) => setVac(r.data)).catch(() => setVac(null));
    api.get(`/companies/${companyId}/wsib-reconciliation?year=${year}`).then((r) => setWsib(r.data)).catch(() => setWsib(null));
  }, [companyId, year]);

  const t = data?.totals;
  const loadWsib = () => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/wsib-reconciliation?year=${year}`).then((r) => setWsib(r.data)).catch(() => setWsib(null));
  };
  const toggleWsibFiled = async (s) => {
    if (s.status === "No premium") return;
    try {
      await api.post(`/companies/${companyId}/remittance-filing`, { kind: "wsib", year, period: s.period, filed: !s.filed });
      loadWsib();
      toast.success(!s.filed ? `${s.period} marked filed` : `${s.period} filing cleared`);
    } catch (e) { toast.error(apiErr(e)); }
  };
  // report-scope query string: single run overrides year
  const rq = scope === "run" && runId ? `run_id=${runId}` : scope === "range" ? `start=${range.start}&end=${range.end}` : `year=${year}`;
  const activeRun = runs.find((r) => r.run_id === runId);
  const scopeLabel = scope === "run"
    ? (activeRun ? `${activeRun.run_number || "Run"} · ${activeRun.pay_date}` : "Select a run")
    : scope === "range" ? `Pay dates ${range.start} → ${range.end}` : `Year-to-date register · ${year}`;

  return (
    <div className="w-full">
      <CompanyBreadcrumb label="Reports" />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Payroll Reports</h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono uppercase tracking-widest">{scopeLabel}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {isMasterUser && <Link to="/reports/trust-balance" data-testid="reports-trust-balance-link" className="inline-flex items-center gap-2 border border-black px-3 py-2.5 text-xs font-mono uppercase tracking-widest hover:bg-black hover:text-white transition-colors"><Landmark className="h-3.5 w-3.5" /> Trust balance</Link>}
          <div className="flex border border-black">
            <button data-testid="reports-scope-year" onClick={() => setScope("year")} className={`px-3 py-2.5 text-xs font-mono uppercase tracking-widest transition-colors ${scope === "year" ? "bg-black text-white" : "bg-white hover:bg-neutral-100"}`}>Annual</button>
            <button data-testid="reports-scope-run" onClick={() => setScope("run")} className={`px-3 py-2.5 text-xs font-mono uppercase tracking-widest border-l border-black transition-colors ${scope === "run" ? "bg-black text-white" : "bg-white hover:bg-neutral-100"}`}>Single run</button>
            <button data-testid="reports-scope-range" onClick={() => setScope("range")} className={`px-3 py-2.5 text-xs font-mono uppercase tracking-widest border-l border-black transition-colors ${scope === "range" ? "bg-black text-white" : "bg-white hover:bg-neutral-100"}`}>Date range</button>
          </div>
          {scope === "range" && (
            <div className="flex items-center gap-1" data-testid="reports-range-pickers">
              <input data-testid="reports-range-start" type="date" value={range.start} max={range.end} onChange={(e) => setRange({ ...range, start: e.target.value })} className="border border-input bg-white px-2 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
              <span className="text-xs text-muted-foreground">→</span>
              <input data-testid="reports-range-end" type="date" value={range.end} min={range.start} onChange={(e) => setRange({ ...range, end: e.target.value })} className="border border-input bg-white px-2 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
            </div>
          )}
          {scope === "run" ? (
            <select data-testid="reports-run-select" value={runId} onChange={(e) => setRunId(e.target.value)} className="border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black min-w-[220px]">
              {runs.length === 0 && <option value="">No finalized runs</option>}
              {runs.map((r) => <option key={r.run_id} value={r.run_id}>{(r.run_number || "Run")} · {r.pay_date} ({r.employee_count ?? r.payslip_count ?? ""} emp)</option>)}
            </select>
          ) : scope === "range" ? null : (
            <input data-testid="reports-year-input" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24 border border-input bg-white px-3 py-2.5 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-black" />
          )}
          <a data-testid="reports-pdf-button" href={companyId ? `${process.env.REACT_APP_BACKEND_URL}/api/reports/pdf?company_id=${companyId}&year=${year}` : undefined} target="_blank" rel="noreferrer" className={`px-4 py-2.5 text-sm font-medium border transition-colors ${scope !== "year" ? "hidden" : ""} ${companyId ? "border-black bg-white hover:bg-black hover:text-white" : "border-neutral-300 text-neutral-400 pointer-events-none"}`}>Register PDF</a>
          <a data-testid="reports-csv-button" href={companyId ? `${process.env.REACT_APP_BACKEND_URL}/api/reports/csv?company_id=${companyId}&year=${year}` : undefined} target="_blank" rel="noreferrer" className={`px-4 py-2.5 text-sm font-medium border transition-colors ${scope !== "year" ? "hidden" : ""} ${companyId ? "border-black bg-white hover:bg-black hover:text-white" : "border-neutral-300 text-neutral-400 pointer-events-none"}`}>Register CSV</a>
          <a data-testid="reports-xlsx-button" href={companyId ? `${process.env.REACT_APP_BACKEND_URL}/api/reports/xlsx?company_id=${companyId}&year=${year}` : undefined} target="_blank" rel="noreferrer" className={`px-4 py-2.5 text-sm font-medium border transition-colors ${scope !== "year" ? "hidden" : ""} ${companyId ? "border-black bg-white hover:bg-black hover:text-white" : "border-neutral-300 text-neutral-400 pointer-events-none"}`}>Register Excel</a>
        </div>
      </div>

      <ReportingWorkspace companyId={companyId} />
      {wsibDlg && <ClosedNoticeDialog wsib={wsibDlg} onClose={() => setWsibDlg(null)} onSent={() => api.get(`/companies/${companyId}/wsib-reconciliation?year=${year}`).then((r) => setWsib(r.data)).catch(() => {})} />}

      <div className="flex flex-wrap gap-1 border-b border-border mb-6" data-testid="reports-tabs">
        {[["reports", "Reports"], ["ytd", "Per-employee YTD earnings"], ["vacation", "Vacation — banked & payouts"], ["wsib", "WSIB Reconciliation"]].map(([id, label]) => (
          <button key={id} data-testid={`reports-tab-${id}`} onClick={() => setTab(id)} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${tab === id ? "border-black text-black" : "border-transparent text-muted-foreground hover:text-black"}`}>{label}</button>
        ))}
      </div>

      {tab === "reports" && (<>
      {scope === "run" && runs.length === 0 && (
        <div className="border border-dashed border-border bg-white px-5 py-4 mb-6 text-sm text-muted-foreground" data-testid="reports-no-runs">No finalized payroll runs yet — the report catalog below runs against a single approved run once you finalize one.</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8">
      <ReportGroup title="Payroll" companyId={companyId} rq={rq} disabled={scope === "run" && !runId} items={[
        ["payroll_register", "Payroll register", "Gross-to-net by employee"],
        ["earnings", "Earnings", "Regular, overtime, holiday & vacation by employee"],
        ["deductions", "Deductions", "Statutory and other deductions withheld"],
        ["benefits", "Employer contributions", "Employer CPP / EI by employee"],
        ["paystubs", "Pay statements summary", "Gross-to-net per statement"],
        ["annual", "Payroll by pay period", "All amounts by pay date"],
        ["ytd", "Year-to-date", "YTD amounts up to the last approved payroll"],
      ]} />
      <ReportGroup title="Statutory & CRA" companyId={companyId} rq={rq} disabled={scope === "run" && !runId} items={[
        ["payroll_taxes", "Payroll taxes", "Income tax, CPP, EI and total remittance"],
        ["remittance", "CRA remittance by pay date", "PD7A source amounts per run"],
        ["workers_comp", "WSIB / Workers' Compensation", "Assessable earnings and premiums"],
      ]} />
      <ReportGroup title="Time & ESA compliance" companyId={companyId} rq={rq} disabled={scope === "run" && !runId} items={[
        ["overtime", "Overtime — ESA", "Weekly OT hours, 1.5× pay, lieu banked, method"],
        ["holiday_pay", "Public holiday pay — ESA", "÷20 base, holiday pay, premium / substitute"],
        ["time_summary", "Hours by time category", "Worked, OT, vacation, sick, leave hours per statement"],
        ["lieu_bank", "Time-in-lieu bank", "Lieu hours earned, used and balance"],
      ]} />
      <ReportGroup title="Accounting & banking" companyId={companyId} rq={rq} disabled={scope === "run" && !runId} items={[
        ["posting_journal", "Posting journal", "Payroll expenses and liabilities"],
        ["deposit_summary", "Deposit summary", "Net pay by payment method"],
        ["payroll_invoice", "Payroll funding summary", "Funds required per payroll"],
        ["payments", "Direct deposit payments", "Every Forte deposit: amount, status, settle date"],
      ]} />
      </div>
      </>)}

      {tab === "ytd" && (<>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 mt-2">
        <Card label="Total Gross" value={t ? money(t.gross) : "—"} icon={Banknote} testid="report-gross" />
        <Card label="Income Tax" value={t ? money(t.income_tax) : "—"} icon={Landmark} accent="text-red-600" testid="report-tax" />
        <Card label="Net Paid" value={t ? money(t.net_pay) : "—"} icon={Banknote} accent="text-emerald-600" testid="report-net" />
        <Card label="Employees" value={t ? t.employee_count : "—"} icon={Users} testid="report-emps" />
      </div>

      <div className="border border-border bg-white">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          <h2 className="font-bold tracking-tight">Per-employee YTD earnings</h2>
          <span className="ml-auto text-xs font-mono text-muted-foreground">{t?.run_count || 0} runs</span>
        </div>
        {data?.employees?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  <th className="px-4 py-3 sticky left-0 bg-white"><SortBtn label="Employee" col="name" sort={empS.sort} onSort={empS.toggle} /></th>
                  <th className="px-4 py-3 text-center"><SortBtn label="Slips" col="slips" sort={empS.sort} onSort={empS.toggle} /></th>
                  <th className="px-4 py-3 text-right"><SortBtn label="Gross" col="gross" sort={empS.sort} onSort={empS.toggle} /></th>
                  <th className="px-4 py-3 text-right"><SortBtn label="Income Tax" col="income_tax" sort={empS.sort} onSort={empS.toggle} /></th>
                  <th className="px-4 py-3 text-right"><SortBtn label="CPP" col="cpp" sort={empS.sort} onSort={empS.toggle} /></th>
                  <th className="px-4 py-3 text-right"><SortBtn label="EI" col="ei" sort={empS.sort} onSort={empS.toggle} /></th>
                  <th className="px-4 py-3 text-right"><SortBtn label="Net" col="net" sort={empS.sort} onSort={empS.toggle} /></th>
                </tr>
              </thead>
              <tbody>
                {empS.sorted.map((e) => (
                  <tr key={e.employee_id} data-testid={`report-row-${e.employee_id}`} className="border-b border-border last:border-0 hover:bg-muted/50">
                    <td className="px-4 py-3 sticky left-0 bg-white font-medium">{e.employee_name}</td>
                    <td className="px-4 py-3 text-center tabular-nums">{e.slips}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{money(e.gross)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-red-600">{money(e.income_tax)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-red-600">{money(e.cpp + e.cpp2)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-red-600">{money(e.ei)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-emerald-600 font-medium">{money(e.net_pay)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-black font-bold bg-neutral-50">
                  <td className="px-4 py-3 sticky left-0 bg-neutral-50">TOTAL</td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-right tabular-nums">{money(t.gross)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-red-600">{money(t.income_tax)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-red-600">{money(t.cpp + t.cpp2)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-red-600">{money(t.ei)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-emerald-600">{money(t.net_pay)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="px-5 py-16 text-center text-sm text-muted-foreground">No payroll data for {year}.</div>
        )}
      </div>
      </>)}

      {tab === "vacation" && (
      <div className="border border-border bg-white mt-6" data-testid="vacation-report">
        <div className="px-5 py-4 border-b border-border flex flex-wrap items-center gap-2">
          <Palmtree className="h-4 w-4" />
          <h2 className="font-bold tracking-tight">Vacation — banked & payouts ({year})</h2>
          <span className="ml-auto text-xs font-mono text-muted-foreground">Balance {vac ? money(vac.totals.current_balance) : "—"}</span>
          <a data-testid="vacation-csv-button" href={companyId ? `${process.env.REACT_APP_BACKEND_URL}/api/reports/vacation.csv?company_id=${companyId}&year=${year}` : undefined} target="_blank" rel="noreferrer" className={`px-3 py-1.5 text-xs font-medium border transition-colors ${companyId ? "border-black bg-white hover:bg-black hover:text-white" : "border-neutral-300 text-neutral-400 pointer-events-none"}`}>Export CSV</a>
          <a data-testid="vacation-xlsx-button" href={companyId ? `${process.env.REACT_APP_BACKEND_URL}/api/reports/vacation.xlsx?company_id=${companyId}&year=${year}` : undefined} target="_blank" rel="noreferrer" className={`px-3 py-1.5 text-xs font-medium border transition-colors ${companyId ? "border-black bg-white hover:bg-black hover:text-white" : "border-neutral-300 text-neutral-400 pointer-events-none"}`}>Export Excel</a>
        </div>
        {vac?.employees?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  <th className="px-4 py-3"><SortBtn label="Employee" col="name" sort={vacS.sort} onSort={vacS.toggle} /></th>
                  <th className="px-4 py-3 text-right"><SortBtn label="Rate" col="rate" sort={vacS.sort} onSort={vacS.toggle} /></th>
                  <th className="px-4 py-3 text-right"><SortBtn label="Accrued (banked)" col="accrued" sort={vacS.sort} onSort={vacS.toggle} /></th>
                  <th className="px-4 py-3 text-right"><SortBtn label="Paid from bank" col="paid" sort={vacS.sort} onSort={vacS.toggle} /></th>
                  <th className="px-4 py-3 text-right"><SortBtn label="Vac. pay (direct)" col="direct" sort={vacS.sort} onSort={vacS.toggle} /></th>
                  <th className="px-4 py-3 text-right"><SortBtn label="Current balance" col="balance" sort={vacS.sort} onSort={vacS.toggle} /></th>
                  <th className="px-4 py-3 text-right">Statement</th>
                </tr>
              </thead>
              <tbody>
                {vacS.sorted.map((e) => (
                  <Fragment key={e.employee_id}>
                    <tr data-testid={`vacation-row-${e.employee_id}`} onClick={() => setOpenEmp(openEmp === e.employee_id ? null : e.employee_id)} className="border-b border-border hover:bg-muted/50 cursor-pointer">
                      <td className="px-4 py-3 font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {openEmp === e.employee_id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          {e.employee_name}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{e.vacation_rate}%</td>
                      <td className="px-4 py-3 text-right tabular-nums text-emerald-600">{money(e.total_accrued)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-amber-700">{money(e.total_paid)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{money(e.total_paid_direct)}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium">{money(e.current_balance)}</td>
                      <td className="px-4 py-3 text-right">
                        <a data-testid={`vacation-statement-${e.employee_id}`} onClick={(ev) => ev.stopPropagation()} href={`${process.env.REACT_APP_BACKEND_URL}/api/reports/vacation/${companyId}/${e.employee_id}/statement.pdf?year=${year}`} target="_blank" rel="noreferrer" className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-black underline underline-offset-2">PDF</a>
                      </td>
                    </tr>
                    {openEmp === e.employee_id && (
                      <tr data-testid={`vacation-detail-${e.employee_id}`} className="bg-neutral-50 border-b border-border">
                        <td colSpan={7} className="px-4 py-3">
                          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Transaction history</div>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-muted-foreground">
                                <th className="py-1">Pay date</th>
                                <th className="py-1">Run</th>
                                <th className="py-1 text-right">Accrued</th>
                                <th className="py-1 text-right">Paid from bank</th>
                                <th className="py-1 text-right">Vac. pay (direct)</th>
                                <th className="py-1">Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {e.transactions.map((t, i) => (
                                <tr key={i} className="border-t border-border">
                                  <td className="py-1 tabular-nums">{t.pay_date}</td>
                                  <td className="py-1 font-mono">{t.run_number || "—"}</td>
                                  <td className="py-1 text-right tabular-nums text-emerald-600">{money(t.accrued)}</td>
                                  <td className="py-1 text-right tabular-nums text-amber-700">{money(t.paid_from_bank)}</td>
                                  <td className="py-1 text-right tabular-nums">{money(t.vacation_pay_direct)}</td>
                                  <td className="py-1 text-muted-foreground">{t.reason || "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-5 py-16 text-center text-sm text-muted-foreground">No vacation activity for {year}.</div>
        )}
      </div>
      )}

      {tab === "wsib" && (
      <div className="border border-border bg-white mt-6" data-testid="wsib-reconciliation">
        <div className="px-5 py-4 border-b border-border flex flex-wrap items-center gap-2">
          <ShieldCheck className="h-4 w-4" />
          <h2 className="font-bold tracking-tight">WSIB Reconciliation — {(wsib?.filing_frequency || "quarterly") === "monthly" ? "monthly" : "quarterly"} ({year})</h2>
          {wsib && <span className="ml-auto text-xs font-mono text-muted-foreground">Premium {money(wsib.totals.premium)} · Max insurable ${Number(wsib.max_insurable).toLocaleString()} · {(wsib.filing_frequency || "quarterly") === "monthly" ? "Monthly" : "Quarterly"} filing</span>}
          <a data-testid="wsib-csv-button" href={companyId ? `${process.env.REACT_APP_BACKEND_URL}/api/companies/${companyId}/wsib-reconciliation.csv?year=${year}` : undefined} target="_blank" rel="noreferrer" className={`px-3 py-1.5 text-xs font-medium border transition-colors ${companyId ? "border-black bg-white hover:bg-black hover:text-white" : "border-neutral-300 text-neutral-400 pointer-events-none"}`}>Export CSV</a>
          <a data-testid="wsib-pdf-button" href={companyId ? `${process.env.REACT_APP_BACKEND_URL}/api/companies/${companyId}/wsib-reconciliation.pdf?year=${year}` : undefined} target="_blank" rel="noreferrer" className={`px-3 py-1.5 text-xs font-medium border transition-colors ${companyId ? "border-black bg-white hover:bg-black hover:text-white" : "border-neutral-300 text-neutral-400 pointer-events-none"}`}>PDF</a>
        </div>
        {wsib?.employees?.length ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border-b border-border">
              {wsib.quarters.map((q) => (
                <div key={q.quarter} data-testid={`wsib-quarter-${q.quarter}`} className="bg-white p-4">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{q.quarter} premium</div>
                  <div className="mt-1 text-xl font-black tabular-nums">{money(q.premium)}</div>
                  <div className="text-[11px] text-muted-foreground tabular-nums">on {money(q.assessable)}</div>
                </div>
              ))}
            </div>
            {wsib.schedule?.length > 0 && (
              <div className="border-b border-border" data-testid="wsib-schedule">
                <div className="px-5 py-2.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground bg-neutral-50">Remittance schedule ({(wsib.filing_frequency || "quarterly") === "monthly" ? "monthly" : "quarterly"}) — due = last day of the month after each period</div>
                <div className="flex flex-wrap gap-px bg-border">
                  {wsib.schedule.map((s) => (
                    <div key={s.period} data-testid={`wsib-sched-${s.period.replace(/\s/g, "-")}`} className="bg-white p-3 flex-1 min-w-[150px]">
                      <div className="text-xs font-medium">{s.period}</div>
                      <div className="text-sm tabular-nums mt-0.5">{money(s.premium)}</div>
                      <div className="text-[10px] text-muted-foreground tabular-nums mt-0.5">insurable {money(s.assessable || 0)}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">due {s.due_date}</div>
                      <button data-testid={`wsib-file-${s.period.replace(/\s/g, "-")}`} onClick={() => toggleWsibFiled(s)} disabled={s.status === "No premium" || !reportsEdit} title={s.status === "No premium" ? "" : "Click to toggle Filed"} className={`inline-block mt-1 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border transition-colors ${STATUS_CLS(s.status)} ${s.status === "No premium" ? "cursor-default" : "cursor-pointer hover:opacity-80"}`}>{s.status}</button>
                      {reportsEdit && s.premium > 0 && (
                        <button data-testid={`wsib-notify-${s.period.replace(/\s/g, "-")}`} onClick={() => setWsibDlg({ companyId, year, period: s.period, label: s.period })}
                          title={s.closed_notice_sent_at ? `Last sent ${fmtDateTime(s.closed_notice_sent_at)} by ${s.closed_notice_sent_by}` : "Email the client the WSIB Premium Remit Notification"}
                          className="mt-2 w-full inline-flex items-center justify-center gap-1 text-[10px] font-medium border border-black bg-white px-2 py-1 hover:bg-black hover:text-white transition-colors">
                          <Mail className="h-3 w-3" /> {s.closed_notice_sent_at ? "Resend WSIB email" : "Email WSIB notice"}
                        </button>
                      )}
                      {s.closed_notice_sent_at && (
                        <div data-testid={`wsib-notify-receipt-${s.period.replace(/\s/g, "-")}`} className={`text-[9px] mt-1 ${s.closed_notice_failed?.length ? "text-red-600" : "text-emerald-700"}`}>
                          Sent {fmtDateTime(s.closed_notice_sent_at)}{s.closed_notice_failed?.length ? ` · ${s.closed_notice_failed.length} bounced` : ""}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    <th className="px-4 py-3"><SortBtn label="Employee" col="name" sort={wsibS.sort} onSort={wsibS.toggle} /></th>
                    <th className="px-4 py-3"><SortBtn label="Class" col="code" sort={wsibS.sort} onSort={wsibS.toggle} /></th>
                    <th className="px-4 py-3 text-right"><SortBtn label="Rate" col="rate" sort={wsibS.sort} onSort={wsibS.toggle} /></th>
                    <th className="px-4 py-3 text-right"><SortBtn label="Q1" col="q1" sort={wsibS.sort} onSort={wsibS.toggle} /></th>
                    <th className="px-4 py-3 text-right"><SortBtn label="Q2" col="q2" sort={wsibS.sort} onSort={wsibS.toggle} /></th>
                    <th className="px-4 py-3 text-right"><SortBtn label="Q3" col="q3" sort={wsibS.sort} onSort={wsibS.toggle} /></th>
                    <th className="px-4 py-3 text-right"><SortBtn label="Q4" col="q4" sort={wsibS.sort} onSort={wsibS.toggle} /></th>
                    <th className="px-4 py-3 text-right"><SortBtn label="Assessable" col="assessable" sort={wsibS.sort} onSort={wsibS.toggle} /></th>
                    <th className="px-4 py-3 text-right"><SortBtn label="Premium" col="premium" sort={wsibS.sort} onSort={wsibS.toggle} /></th>
                  </tr>
                </thead>
                <tbody>
                  {wsibS.sorted.map((e) => (
                    <tr key={e.employee_id} data-testid={`wsib-row-${e.employee_id}`} className="border-b border-border last:border-0 hover:bg-muted/50">
                      <td className="px-4 py-3 font-medium">{e.employee_name}</td>
                      <td className="px-4 py-3 font-mono text-xs">{e.code}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{Number(e.rate).toFixed(2)}%</td>
                      <td className="px-4 py-3 text-right tabular-nums">{money(e.assessable[0])}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{money(e.assessable[1])}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{money(e.assessable[2])}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{money(e.assessable[3])}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{money(e.total_assessable)}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium text-red-600">{money(e.total_premium)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-black font-bold bg-neutral-50">
                    <td className="px-4 py-3" colSpan={3}>TOTAL PREMIUM</td>
                    <td className="px-4 py-3 text-right tabular-nums" data-testid="wsib-total-q1">{money(wsib.quarters[0].assessable)}</td>
                    <td className="px-4 py-3 text-right tabular-nums" data-testid="wsib-total-q2">{money(wsib.quarters[1].assessable)}</td>
                    <td className="px-4 py-3 text-right tabular-nums" data-testid="wsib-total-q3">{money(wsib.quarters[2].assessable)}</td>
                    <td className="px-4 py-3 text-right tabular-nums" data-testid="wsib-total-q4">{money(wsib.quarters[3].assessable)}</td>
                    <td className="px-4 py-3 text-right tabular-nums" data-testid="wsib-total-assessable">{money(wsib.totals.assessable)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-red-600" data-testid="wsib-total-premium">{money(wsib.totals.premium)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        ) : (
          <div className="px-5 py-16 text-center text-sm text-muted-foreground">No WSIB-applicable earnings for {year}, or no WSIB rates configured in Company settings.</div>
        )}
      </div>
      )}
    </div>
  );
}

function ReportGroup({ title, items, companyId, rq, disabled }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const ok = companyId && !disabled;
  return (
    <div className="mb-8" data-testid={`report-group-${title.slice(0, 8).toLowerCase().replace(/\s/g, "-")}`}>
      <h2 className="text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground mb-3">{title}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(([rtype, name, desc]) => (
          <div key={rtype} data-testid={`report-card-${rtype}`}
            className={`group border border-border bg-white p-4 ${ok ? "hover:border-black" : "opacity-50 pointer-events-none"} transition-colors`}>
            <a href={ok ? `${API}/api/companies/${companyId}/reports/${rtype}.pdf?${rq}` : undefined}
              target="_blank" rel="noreferrer" data-testid={`report-pdf-${rtype}`} className="flex items-start gap-3">
              <FileDown className="h-4 w-4 mt-0.5 text-muted-foreground group-hover:text-black shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-bold tracking-tight">{name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
              </div>
            </a>
            <div className="mt-2 pt-2 border-t border-border/60 flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              <span>PDF</span>
              <a href={ok ? `${API}/api/companies/${companyId}/reports/${rtype}.xlsx?${rq}` : undefined}
                target="_blank" rel="noreferrer" data-testid={`report-xlsx-${rtype}`} className="hover:text-black underline underline-offset-2">Excel</a>
              <a href={ok ? `${API}/api/companies/${companyId}/reports/${rtype}.csv?${rq}` : undefined}
                target="_blank" rel="noreferrer" data-testid={`report-csv-${rtype}`} className="hover:text-black underline underline-offset-2">CSV</a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Card({ label, value, accent, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="border border-border bg-white p-5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      <div className={`mt-2 text-2xl font-black tracking-tight tabular-nums ${accent || ""}`}>{value}</div>
    </div>
  );
}
